import { Injectable } from '@angular/core';
import { User, UserRole } from './user';
import { SessionStateService } from './session-state.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import * as AppConfig from '../../app.config';
import * as api from '../../api';
import * as Rx from 'rxjs';
import { UserSettings } from "../../api/model/userSettings";
import { SortFilterDto } from "../../api/model/sortFilterDto";
import { AdminUserList } from "../../api/model/adminUserList";
import { ApiRequestDto } from '../../api';
import { lastValueFrom } from 'rxjs';

/**
 * Service for information about the application user, and authentication.
 */
@Injectable()
export class UserService {
    private static readonly STATE_KEY = 'user';
    private static readonly AUTH_CLIENT_ID =  'SPAAuthApp';
    /** How many milliseconds before session expiration shall we try to extend it? */
    private static readonly EXTEND_SESSION_EARLY_MS = 30000;
    public static readonly  GABAPENTIN_STATE_KEY = 'includeGabapentinCb';

    /**
     * Subject to observe to witness changes to the current user.
     * Emits a User object upon login, and null upon logout.
     */
    user$: Rx.BehaviorSubject<User> = new Rx.BehaviorSubject(null);

    /**
     * Subject to observe to witness changes to the user search results.
     * Call .next() to change the current selections.
     */
    public readonly adminUserSearchResults$ = new Rx.BehaviorSubject<AdminUserList[]>(null);

    /** URI to perform authentication */
    private readonly authURI: string;
    private readonly jwtHelper = new JwtHelperService();

    private user: User = null;


    constructor(
        private sessionState: SessionStateService,
        private userApi: api.UserService,
        private httpClient: HttpClient,
    ) {
        this.authURI = AppConfig.baseApiUrl() + "/api/token";

        // Upon page reload, check for authenticated user in storage.
        this.user = this.sessionState.load(UserService.STATE_KEY);
        if (this.user) {
            console.log("UserService loaded storedUser", this.user);
            setTimeout(() => this.extendSession());
        }
        this.user$.next(this.user);
    }

    get name(): string|null {
        return this.user ? this.user.name : null;
    }

    get firstName(): string|null {
        return this.user ? this.user.firstName : null;
    }

    get lastName(): string|null {
        return this.user ? this.user.lastName : null;
    }

    get userName(): string|null {
        return this.user ? this.user.username : null;
    }

    get loginName() : string|null {
        return this.user ? this.user.loginName : null;
    }

    get email(): string|null {
        return this.user ? this.user.email : null;
    }

    get phoneNumber(): string|null {
        return this.user ? this.user.phoneNumber : null;
    }

    /** Is a user authenticated? */
    get isAuthenticated(): boolean {
        if (!this.user || this.user.role === undefined)
            return false;

        if (this.user.jwt && this.jwtHelper.isTokenExpired(this.user.jwt.access_token))
            return false;

        return true;
    }

    /** Is a user authenticated? */
    get isImpersonating(): boolean {
        return this.user ? this.user.isImpersonating : false;
    }

    /** Is a user authenticated via single sign on? */
    get isSSOLogin(): boolean {
        return this.user ? this.user.isSSOLogin : false;
    }

    //We are ordering the User Roles by highest to lowest access so we know that the first element is the highest level of access.

    /** Is a user authenticated as a Director of Nursing? */
    get isDON(): boolean {
        return this.user && this.user.role[0] === "DON";
    }

    /** Is a user authenticated as a nurse? */
    get isNurse(): boolean {
        return this.user && this.user.role[0] === "Nurse";
    }

    /** Is a user authenticated as a corporate user? */
    get isCorporate(): boolean {
        return this.user && this.user.role[0] === "Corporate";
    }

    get isUserAdmin(): boolean {
        return this.user && this.user.role.includes("UserAdministration");
    }

    get isBusinessOffice(): boolean {
        return this.user && this.user.role.includes("BusinessOffice");
    }
    
    get isDrugPriceQuoteRole(): boolean {
        return this.user && this.user.role.includes("DrugPriceQuote");
    }

    get isInvoiceApprovalRole(): boolean {
        return this.user && this.user.role.includes("InvoiceApproval");
    }

    /** Retrieve User model of the current authenticated user */
    get details(): User|null {
        return this.user;
    }

    /** Does the user have access to nurse resources? */
    get hasNurseAccess(): boolean {
        return this.isNurse;
    }

    /** Does the user have access to Director of Nursing resources? */
    get hasDONAccess(): boolean {
        return this.isDON;
    }

    /** Does the user have access to corporate resources? */
    get hasCorporateAccess(): boolean {
        return this.isCorporate;
    }

    get hasUserAdminAccess(): boolean {
        return this.isUserAdmin;
    }

    /** Does the user needs to reset the password? */
    get needPasswordChange(): boolean {
        return this.user ? this.user.needPasswordChange : false;
    }

    get roles(): UserRole[] {
        return this.user ? this.user.role : null;
    }

    get isDemoUser(): boolean {
        return this.user.username.substring(0, 8).toUpperCase() === "DEMOUSER";
    }

    /**
     * Login a user by email/username and password.
     *
     * @return Promise with the User upon success; reject string upon failure.
     */
    async loginViaCredentials(username: string, password: string): Promise<User> {
        if (!username || !password)
            return Promise.reject<User>("Invalid login");
       
        let body: HttpParams = new HttpParams();
        body = body.set('username', encodeURIComponent(username));
        body = body.set('password', encodeURIComponent(password));
        body = body.set('grant_type', 'password');
        body = body.set('client_id', UserService.AUTH_CLIENT_ID);

        let user: User = await lastValueFrom(this.doPost(body))
            .then(response => {
                if (this.user && this.user.ssoRedirect) {
                    let ret = new Promise<User> ((resolve) => { resolve(this.user)});
                    return ret;
                }
                return this.loginChainProcessJWT(response);
            })
            .catch(error => {
                // Clear any partial success data
                this.logout().then();

                if (error.error === "SSORedirect") {
                    //redirect url comes in response body error_description
                    this.initUser();
                    this.user.ssoRedirect = error.error_description;
                    return this.user;
                }
                throw error;            
            });
        let userPromise = new Promise<User>((resolve) => resolve(user));
        return userPromise;
    }

    /**
     * Login a user via a valid JWT refresh token.
     *
     * @return Promise with the User upon success; reject string upon failure.
     */
    async loginByJWT(jwtRefreshToken: string): Promise<User> {
        let body: HttpParams = new HttpParams();
        body = body.set('refresh_token', jwtRefreshToken);
        body = body.set('grant_type', 'refresh_token');
        body = body.set('client_id', UserService.AUTH_CLIENT_ID);

        let user = await lastValueFrom(this.doPost(body))
            .catch((error) => {
                console.log("Login via JWT refresh failed", error.error || error.error_description);
                throw "Please log in.";
            })
            .then((response) => {
                return this.loginChainProcessJWT(response);
            });
        let userPromise = new Promise<User>((resolve) => resolve(user));
        return userPromise;            
    }

    /**
     * Login chain of actions: Received a JWT - continue.
     * @returns {Promise<User>}
     */
    private async loginChainProcessJWT(jwt: any): Promise<User> {
        if (jwt == null || !jwt.userName)
            throw 'Invalid server response';

        let decodedToken = this.jwtHelper.decodeToken(jwt.access_token);

        this.initUser();
        this.user.username = jwt.userName;
        this.user.isImpersonating = (decodedToken.imp === "1");
        this.user.isSSOLogin = (decodedToken.sso === "1");
        this.user.jwt = jwt;

        let user = await lastValueFrom(this.userApi.userGetUser())
            .then(apiUser => {
                apiUser.UserName = jwt.userName;
                let user = this.loginChainProcessUserDetail(apiUser);
                return user;
                });
        let userPromise = new Promise<User>((resolve) => resolve(user));                
        return userPromise;
    }

    private initUser() {
        this.user = {
            username: '',
            loginName: '',
            name: '?',
            firstName: '?',
            lastName: '?',
            role: ["Nurse"],
            isImpersonating: false,
            id:'0',
            email: "test@test.com",
            phoneNumber:'',
            userTimeZone: 'Pacific Standard Time',
            jwt: {access_token: '', refresh_token: ''},
            ssoRedirect: '',
            isSSOLogin: false            
        };
    }
    /**
     * Login chain of actions: Received User detail
     * @param apiUser
     * @returns {User}
     */
    private loginChainProcessUserDetail(apiUser: api.User): User {
        if (!apiUser.FirstName || !apiUser.LastName) {
            this.logout().then();
            throw "Missing user account details.  Please contact your facility's MyRemedi administrator.";
        }

        this.user.loginName = apiUser.LoginName;
        this.user.firstName = apiUser.FirstName;
        this.user.lastName = apiUser.LastName;
        this.user.name = apiUser.FirstName + " " + apiUser.LastName;
        this.user.role = apiUser.Role.map(x => x as UserRole)
        this.user.email = apiUser.EmailAddress;
        this.user.phoneNumber = apiUser.PhoneNumber;    
        this.user.needPasswordChange = apiUser.NeedPasswordReset;
        this.user.username = apiUser.UserName;
        this.user.userTimeZone = apiUser.UserTimeZone;

        return this.loginChainFinish();
    }

    /**
     * Login chain of actions: Process state after successful login.
     * @param user new User value
     * @returns user
     */
    private loginChainFinish(user?: User): User {
        // If User passed in, use it. Otherwise we assume this.user is already assigned.
        if (user)
            this.user = user;

        // Keep a copy in session state, so that it can be restored upon a page reload.
        this.sessionState.prepareForUser(this.user.username);
        this.sessionState.save(UserService.STATE_KEY, this.user);
        this.sessionState.save(UserService.GABAPENTIN_STATE_KEY, false);
        console.log("Logged in as " + this.user.username);

        this.scheduleSessionExtension();

        // Publish to listeners 
        this.user$.next(this.user);

        // Echo back as return value
        return this.user;
    }

    /**
     * Log the user out - ending the session
     *
     * @return Promise that completes with null.
     */
    logout(): Promise<void> {
        // Clear data
        this.user = null;
        this.sessionState.remove(UserService.STATE_KEY);
        this.sessionState.remove(UserService.GABAPENTIN_STATE_KEY);
        // Publish to listeners 
        this.user$.next(null);

        return Promise.resolve(null);
    }

    getUserList(facilities: string[],status: string,  sorts: SortFilterDto, displayTimeZone: string){
        const requestParams: ApiRequestDto = { 
            OrgList: facilities,
            SortsAndFilters: sorts
        };
        return this.userApi.userGetUserList(requestParams, status, displayTimeZone);
    }

    getUserListWithExcel(facilities: string[],status: string,  sorts: SortFilterDto, displayTimeZone: string){
        const requestParams: ApiRequestDto = { 
            OrgList: facilities,
            SortsAndFilters: sorts
        };
        return this.userApi.userGetUserListWithExcel(requestParams, status, displayTimeZone);
    }

    getCurrentUserProfile(){
        return this.userApi.userGetUserSettingsForProfileEdit();
    }

    getCurrentUserNotificationSettings() {
        return this.userApi.userGetUserSettingsForNotificationsEdit();
    }

    getUserProfile(username: string){
        return this.userApi.userGetUserSettings(encodeURIComponent(username));
    }

    getNewUserProfile(){
        return this.userApi.userGetUserSettings('');
    }

    getUserFacilities(username: string){
        return this.userApi.userGetUserUnits(encodeURIComponent(username));
    }

    updateUser(settings: UserSettings, allowImpersonation: boolean){
        if (allowImpersonation){
            return this.userApi.userEditUserAllowImpersonation(settings);    
        }
        return this.userApi.userEditUser(settings);
    }

    addUser(settings: UserSettings){
        return this.userApi.userAddUser(settings);
    }

    getUserTimezones(){
        return this.userApi.userGetTimeZones();
    }

    changePassword(currentPassword: string, newPassword: string, confirmPassword: string){
        return this.userApi.userChangePassword(currentPassword, newPassword, confirmPassword);
    }

    sendPhoneCode(phoneNumber: string){
        return this.userApi.userSendPhoneCode(phoneNumber);
    }

    confirmNumber(){
        return this.userApi.userConfirmNumber();
    }

    validatePhoneCode(phoneCode: string){
        return this.userApi.userValidateCode(phoneCode);
    }
    resetPassword(userName: string){
        return this.userApi.userResetPassword(encodeURIComponent(userName));
    }  
    changeUserActiveStatus(userName: string, isActive: boolean){
        return this.userApi.userChangeUserActiveStatus(encodeURIComponent(userName), isActive);
    }
    checkDuplicateUserName(userName: string){
        return this.userApi.userCheckDuplicateUsername(encodeURIComponent(userName));
    }
    
    refreshUser(apiUser: api.User){
        this.user.loginName = apiUser.LoginName;
        this.user.firstName = apiUser.FirstName;
        this.user.lastName = apiUser.LastName;
        this.user.name = apiUser.FirstName + " " + apiUser.LastName;
        this.user.role = apiUser.Role.map(x => x as UserRole)
        this.user.email = apiUser.EmailAddress;
        this.user.phoneNumber = apiUser.PhoneNumber;
        this.user.needPasswordChange = apiUser.NeedPasswordReset;
        this.user.username = apiUser.UserName; 
        this.user.userTimeZone = apiUser.UserTimeZone;
        
        this.user$.next(this.user);
        this.sessionState.save(UserService.STATE_KEY, this.user);
    }

    /**
     * Schedule extendSession() to run before the session expires.
     */
    private scheduleSessionExtension() {
        if (this.user && this.user.jwt && this.user.jwt.access_token) {
            let expDate: Date = this.jwtHelper.getTokenExpirationDate(this.user.jwt.access_token);
            let deltaMs = expDate.getTime() - Date.now() - UserService.EXTEND_SESSION_EARLY_MS;
            if (deltaMs > 0) {
                setTimeout(() => this.extendSession(), deltaMs);
            }
        }
    }

    /**
     * Extend the user's session authentication.
     */
    private extendSession() {
        if (this.user && this.user.jwt && this.user.jwt.refresh_token) {
            let body: HttpParams = new HttpParams();
            body = body.set('refresh_token', this.user.jwt.refresh_token);
            body = body.set('grant_type', 'refresh_token');
            body = body.set('client_id', UserService.AUTH_CLIENT_ID);
    
            lastValueFrom(this.doPost(body))
                .then(response => {
                    console.log("Refresh JWT response", response);
    
                    if (this.user && response) {
                        this.user.jwt = response;
                        this.sessionState.save(UserService.STATE_KEY, this.user);
                        console.log(this.user.username + " authentication extended");
                        this.scheduleSessionExtension();
                    }
                })
                .catch(error => {
                        // Log and rethrow
                        console.log("Refresh JWT failed", error.error || error.error_description);
                        //Retreat to login page
                        this.user = null;
                        this.user$.next(null);
                });
        }
    }

    /*
    *   Returns the possessive form of the user's full name:
    *   i.e. "John Smith's" or "Jane Harris'"
    */
    public static getUserFullnamePossessive(user: AdminUserList): string {
        let fullName = user.FirstName + " " + user.LastName;
        if (fullName.toLocaleLowerCase().endsWith("s")) {
            fullName = fullName + "'";
        }
        else {
            fullName = fullName + "'s";
        }
        return fullName;
    }

    private doPost(requestParams: HttpParams, observe: any = 'body', reportProgress: boolean = false ): Rx.Observable<any> {
        let defaultHeaders = new HttpHeaders();

        if (requestParams === null || requestParams === undefined) {
            throw new Error('Required parameter requestParams was null or undefined when calling doPost.');
        }

        let headers = defaultHeaders;

        const httpHeaderAcceptSelected: string = 'application/json, text/plain, */*';
        headers = headers.set('Accept', httpHeaderAcceptSelected);

        const httpContentTypeSelected: string = 'application/x-www-form-urlencoded';
        headers = headers.set('Content-Type', httpContentTypeSelected);

        let post$ = this.httpClient.post<HttpParams>(this.authURI,
            requestParams,
            {
                headers: headers,
                observe: observe
            }
        );
        return post$;
    }
}
